// Event-emitter facade over the reducer (tauric's EditorStore pattern, not
// Redux). The store owns the command log; state is always derived from it, so
// save, replay and — from Phase 4 — network sync are the same mechanism.

import type { Side } from "src/engine/rules/board";
import {
  applyCommand,
  type Command,
  type GameState,
  IllegalCommandError,
  newGame
} from "src/engine/rules/commands";
import { hashState } from "src/engine/rules/hash";
import {
  changedSquares,
  cutLog,
  planUndo,
  type UndoPlan,
  type UndoRefusal
} from "src/engine/rules/turns";
import type { GameMode } from "src/api/intents";
import { GameHistory } from "./history";
import { modeConfig } from "./modes";
import { planRewind, planRoundBack, type RewindRefusal } from "./rewind";

type Listener = () => void;

/**
 * Why Undo would do nothing, said by the store rather than by the pure walk.
 *
 * **Two reasons more than `UndoRefusal`, and both are facts about this store
 * rather than about the log.** Looking through history is the first (§29 U4):
 * the button is greyed with the reason rather than hidden, and the refusal
 * comes from here so the chrome and the cut cannot disagree about it.
 *
 * The second is a log that does not replay.
 * `planUndo` replays from `base` and `applyCommand` throws on a command the
 * board no longer allows — which is *right* in `src/engine/rules/`, where a broken
 * log is exceptional and U3's server wants the exception. It is wrong here:
 * the chrome asks this question during render, so the throw took React's whole
 * tree down and the page went blank (CI's board smoke, 2026-09-09). A store
 * answers the chrome with a refusal, never with an exception.
 */
export type StoreUndoRefusal = UndoRefusal | "history-broken" | "viewing-history" | "undo-off";

/**
 * What the player is looking at, when it is not the live board (§29 U4).
 *
 * **One object on the snapshot rather than four fields**, and rebuilt only
 * when one of them moves: every zone reads it through `useGameSelect`, which
 * re-renders on identity, and a fresh object per dispatch would re-render the
 * whole of the chrome for a game nobody is scrubbing.
 */
export interface HistoryView {
  /** Which step is on screen, or **null** while that is the live board. */
  view: number | null;
  /** The board to draw — chrome and renderer draw `viewed ?? state`. */
  viewed: GameState | null;
  /** How many boards there are to look at: First, then one per step. */
  count: number;
  /** Log commands landed since the view was opened — Latest's "+N". */
  ahead: number;
  /**
   * The squares the step that reached this board changed — the change tint
   * (§29 U4, §3.11), **measured against the step before it** and not against
   * whatever happened to be on screen. Pressing First is a jump, and lighting
   * everything that has changed all game is not "what just changed".
   *
   * **`null` and `[]` are different claims, and the tint no longer fades.**
   * `null` is "nothing to say" — leave whatever is lit standing, which is what
   * `pass` and every command that touches no square want. `[]` is "light
   * nothing", which First means (a jump to a board nothing changed to reach)
   * and which a new game, a load and a restore mean (the board on screen is
   * not a continuation of the last one, so its tint belongs to a game that is
   * gone). With a fade the two were indistinguishable because everything
   * cleared itself in a second; without one they are the whole difference
   * between a stale highlight and a missing one.
   *
   * Set by `setView`, by a snap back, by undo and by rewind; `null` on an
   * ordinary dispatch, because a diff per command is a board sweep nobody
   * asked for.
   */
  changed: readonly number[] | null;
}

const NO_SQUARES: readonly number[] = Object.freeze([]);
const LIVE: HistoryView = {
  view: null,
  viewed: null,
  count: 1,
  ahead: 0,
  changed: null
};

export interface GameSnapshot {
  state: GameState;
  log: readonly Command[];
  hash: number;
  /** The history preview (§29 U4). `LIVE` for a game nobody is looking back over. */
  history: HistoryView;
}

export class GameStore {
  private snapshot: GameSnapshot;
  private listeners = new Set<Listener>();
  /**
   * The state the log is measured from.
   *
   * Undo is truncate-and-replay, and replay has to start *somewhere*. It used
   * to be safe to assume `newGame()`, because that is where every log began —
   * and then `restore` landed a run from a snapshot with an empty log, which
   * is a base that is not `newGame()` and never can be. Remembering it is one
   * field and makes replay correct in both cases; deriving it would mean
   * guessing, and a wrong guess is a board that silently reverts to a
   * different game.
   */
  private base: GameState;
  /**
   * The last plan `undoPlan` made, keyed on the log it was made from. `null`
   * where it is the log itself that is broken.
   *
   * **Memoised here rather than in the caller**, on `legalMoves`' argument: the
   * answer costs a replay of the log, the chrome asks it on every render to
   * decide what to grey, and a `useMemo` in the zone would have to list
   * `log` as a dependency it never reads — which is the one lint rule this
   * codebase treats as load-bearing. The log array's identity is the cache
   * key because every mutator builds a new one, and `base` only ever moves
   * with it.
   *
   * **The whole plan and not the refusal alone**, so the click and the greying
   * come from one replay and cannot disagree about the board they measured —
   * `planUndo`'s own reason for existing, one caller further out.
   */
  private asked: { log: readonly Command[]; side: Side; plan: UndoPlan | null } | null = null;
  /** `roundBack`'s memo, on `asked`'s terms. */
  private backed: { log: readonly Command[]; side: Side; upto: number | null } | null = null;
  /**
   * The boards this log passed through, and the memo of them (§29 U4).
   *
   * Fed one state per dispatch rather than replayed, so history costs an
   * integer comparison on the ordinary path. Thrown away and rebuilt wherever
   * the log is truncated or replaced, which is the whole of its invalidation.
   */
  private steps: GameHistory;
  /**
   * Which step is being looked at, by whom, and from what log length.
   *
   * **The viewer's side is held with the step** because snap-back is a rule
   * about *whose* move landed: another side's move returns you to the live
   * board, and the store is the only place that sees a command arrive from
   * either the local seat or the room.
   *
   * **`since` is the log length when the view was *opened*** — Latest's "+N"
   * measures from there and not from the step, because stepping back is not
   * falling behind: pressing Back on a game nobody else has touched must read
   * zero, and Back twice must still read zero. Stepping between steps keeps
   * the original `since`, since nothing landed by pressing a button.
   */
  private viewer: { step: number; side: Side; since: number } | null = null;
  private mode: GameMode | undefined;

  constructor(initial: GameState = newGame()) {
    this.snapshot = { state: initial, log: [], hash: hashState(initial), history: LIVE };
    this.base = initial;
    this.steps = new GameHistory(initial);
  }

  getSnapshot = (): GameSnapshot => this.snapshot;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * Apply a command. Returns false when the rules reject it — the UI uses this
   * to ignore an illegal click rather than to crash.
   */
  dispatch(command: Command): boolean {
    const before = this.snapshot.state;
    let next: GameState;
    try {
      next = applyCommand(before, command);
    } catch (error) {
      if (error instanceof IllegalCommandError) return false;
      throw error;
    }

    this.steps.push(command, next);
    // **Snap back when somebody else moves** (§29 U4). The mover is the side
    // that held the clock *before* the command — `position.turn` has passed on
    // by now — and a turn holding several moves snaps on each of them, since
    // each is a board the viewer is now further behind. Construction and
    // policy advance no ply and leave the view where it is, which is what the
    // Latest counter is for. A fresh game is a fresh list of steps, so a view
    // into the old one has nothing left to point at.
    // `null` is "leave the tint alone" and `[]` is "clear it" — see
    // `HistoryView.changed`. A new game clears whether or not anybody was
    // looking back: the lit squares belong to a board that no longer exists.
    let snapped: readonly number[] | null = command.type === "new-game" ? NO_SQUARES : null;
    if (
      this.viewer !== null &&
      (command.type === "new-game" ||
        (next.position.ply > before.position.ply && before.position.turn !== this.viewer.side))
    ) {
      this.viewer = null;
      // **A snap back is Latest pressed for you, so it tints what Latest
      // would**: the live board against the step before it — the move that
      // snapped you. Measured after the push, so that step is this command's,
      // and the boards are not consecutive from where the viewer was standing,
      // which is why the renderer cannot find this for itself.
      snapped = this.changedInto(null, next);
    }
    this.commit(next, [...this.snapshot.log, command], snapped);
    return true;
  }

  /**
   * Look at step `step`, or at the live board when it is null.
   *
   * Clamped, and **the last step is Latest**: it is the live board by another
   * name, so forward off the end lands on the live game rather than on a
   * frozen copy of it that would never see the next move.
   */
  setView(step: number | null, side: Side): void {
    const last = this.steps.count - 1;
    const next =
      step === null || step >= last
        ? null
        : {
            step: Math.max(0, Math.min(step, last)),
            side,
            // Already viewing keeps the baseline it opened with.
            since: this.viewer?.since ?? this.snapshot.log.length
          };
    if (next === null && this.viewer === null) return;
    if (next !== null && this.viewer?.step === next.step && this.viewer.side === next.side) return;
    this.viewer = next;
    this.snapshot = {
      ...this.snapshot,
      history: this.viewOf(this.changedInto(next?.step ?? null))
    };
    for (const listener of this.listeners) listener();
  }

  /**
   * What the step that reached this board changed.
   *
   * **Latest is measured against the last step**, so returning to the live
   * board lights the move you were behind — plus anything built since, which
   * is exactly what you missed while you were reading.
   */
  private changedInto(
    step: number | null,
    live: GameState = this.snapshot.state
  ): readonly number[] {
    const at = step === null ? this.steps.count - 1 : step;
    if (at <= 0) return NO_SQUARES;
    const to = step === null ? live : this.steps.step(step).state;
    return changedSquares(this.steps.step(at - 1).state, to);
  }

  /**
   * A new snapshot for a board the log now reaches, and everyone told.
   *
   * **`changed` is the tint, and the store is its only source** (§29 U4). The
   * renderer works out a played move's two squares and nothing else: a board
   * that changed without a move — an undo, a rewind, a snap back to live — is
   * a diff only the store can take, because only it has the board before.
   * **`null` by default**, which is "leave what is lit standing": an ordinary
   * command that changed no square — `pass` above all — must not put the last
   * change's tint out. A board that *replaces* the game rather than continuing
   * it passes `NO_SQUARES` and clears.
   */
  private commit(
    state: GameState,
    log: readonly Command[],
    changed: readonly number[] | null = null
  ): void {
    // The *new* log's length, because `this.snapshot` is still the old one here.
    const history = this.viewOf(changed, log.length);
    this.snapshot = { state, log, hash: hashState(state), history };
    for (const listener of this.listeners) listener();
  }

  /**
   * The history slice, reusing the last one whenever nothing in it moved.
   *
   * Identity is the change test every reader of the snapshot uses, so a fresh
   * object per dispatch would re-render the whole of the chrome on every
   * command of a game nobody is scrubbing.
   */
  private viewOf(
    changed: readonly number[] | null = null,
    length: number = this.snapshot.log.length
  ): HistoryView {
    const count = this.steps.count;
    const viewer = this.viewer;
    const next: HistoryView =
      viewer === null
        ? { view: null, viewed: null, count, ahead: 0, changed }
        : {
            view: viewer.step,
            viewed: this.steps.step(viewer.step).state,
            count,
            // Landed since the view opened — not since the step, which would
            // count the log the player scrolled back over.
            ahead: length - viewer.since,
            changed
          };
    const was = this.snapshot.history;
    return was.view === next.view &&
      was.viewed === next.viewed &&
      was.count === next.count &&
      was.ahead === next.ahead &&
      was.changed === next.changed
      ? was
      : next;
  }

  /**
   * Adopt a state outright, with an empty log.
   *
   * For a loaded save, which is a *snapshot* rather than a log (see
   * `runSave.ts`). The log starts empty and grows from here: it is still the
   * network protocol and still the replay, it simply no longer reaches back
   * past the load — which is the trade a snapshot makes.
   */
  restore(state: GameState): void {
    this.base = state;
    this.steps = new GameHistory(state);
    this.viewer = null;
    // A restored snapshot is a different game; whatever is lit is the last
    // one's. Same for `load` below.
    this.commit(state, [], NO_SQUARES);
  }

  /** Rebuild from a log — the shape reconnect and load both use. */
  load(log: readonly Command[]): void {
    const base = newGame();
    const steps = new GameHistory(base);
    let state = base;
    for (const command of log) {
      state = applyCommand(state, command);
      steps.push(command, state);
    }
    this.base = base;
    this.steps = steps;
    this.viewer = null;
    this.commit(state, [...log], NO_SQUARES);
  }

  /**
   * Why Undo would do nothing, or null when it would (§29 U2).
   *
   * **The store is asked because the inputs are private.** `base` is the one
   * thing about the store that cannot be derived, and a caller that could read
   * it could also replay from the wrong place — so the question goes in and a
   * refusal comes out, which is the shape `rewindCard` already has. The chrome
   * greys with this answer and `undo` cuts with it, so they cannot disagree.
   */
  /**
   * Which mode this game was opened in (§31.1 Q2): its `MODE_CONFIG` row may
   * turn Undo off. Set by the shell; absent is the default row.
   */
  setMode(mode: GameMode | undefined): void {
    this.mode = mode;
  }

  /** The mode `setMode` last set, for a save that must put it back. */
  modeOf(): GameMode | undefined {
    return this.mode;
  }

  undoRefusal(side: Side): StoreUndoRefusal | null {
    // **The mode before everything**: a mode with no undo has nothing to ask.
    if (!modeConfig(this.mode).undo) return "undo-off";
    // **The screen before the log.** Undo cuts the *live* game, and the board
    // in front of the player is not it — so the answer is what they are doing
    // rather than what the log would allow, and the button greys with that.
    if (this.viewer !== null) return "viewing-history";
    const plan = this.undoPlan(side);
    if (plan === null) return "history-broken";
    // Training reaches past End Turn (§31 G1): nothing left this turn, so
    // the previous round is what Undo hands back.
    if (plan.why === "nothing-to-take-back" && this.roundBack(side) !== null) return null;
    return plan.why;
  }

  /**
   * Where Training's Undo past End Turn lands (`planRoundBack`), or null —
   * null outright unless the mode's row says `undoPast`. Memoised on the log
   * like `undoPlan`, since the chrome asks it every render.
   */
  private roundBack(side: Side): number | null {
    if (!modeConfig(this.mode).undoPast) return null;
    const log = this.snapshot.log;
    const memo = this.backed;
    if (memo !== null && memo.log === log && memo.side === side) return memo.upto;
    let upto: number | null;
    try {
      upto = planRoundBack(this.base, log, side);
    } catch (error) {
      if (!(error instanceof IllegalCommandError)) throw error;
      upto = null;
    }
    this.backed = { log, side, upto };
    return upto;
  }

  /**
   * The plan for this log and this side, or **null** where the log does not
   * replay — memoised, so a click does not replay what the render already did.
   *
   * **The only place `planUndo` is called**, which is what stops the button's
   * sentence and the button's effect coming from two different walks.
   * `IllegalCommandError` is caught because this is asked during render;
   * anything else is a bug in the reducer and is left to throw.
   */
  private undoPlan(side: Side): UndoPlan | null {
    const log = this.snapshot.log;
    const asked = this.asked;
    if (asked !== null && asked.log === log && asked.side === side) return asked.plan;

    let plan: UndoPlan | null;
    try {
      plan = planUndo(this.base, log, side);
    } catch (error) {
      if (!(error instanceof IllegalCommandError)) throw error;
      plan = null;
      // Once per broken log — the memo below sees to that — because a silent
      // "Undo is unavailable" is a bug nobody finds. Naming the command that
      // refused is the whole of the diagnosis: it is always something that
      // reached the state without going through `applyCommand`.
      console.error(
        `GameStore: the command log does not replay, so Undo is unavailable. ${this.brokenAt(log)}: ${error.message}`
      );
    }
    this.asked = { log, side, plan };
    return plan;
  }

  /**
   * Which command refused, in words for the console.
   *
   * A second replay, paid only on the broken path and only once per log —
   * `walkTurns` throws the reducer's message and not the index, and the index
   * is the half that says *where* to look.
   */
  private brokenAt(log: readonly Command[]): string {
    let state = this.base;
    for (let i = 0; i < log.length; i++) {
      try {
        state = applyCommand(state, log[i]);
      } catch {
        return `command ${i} of ${log.length} (${log[i].type})`;
      }
    }
    return "no command refused on a second replay";
  }

  /**
   * Take back this turn's last action, by replaying the log without it.
   *
   * **Truncate-and-replay is the only correct undo here**, and the reason is
   * the reason the log exists: it is the save format, the replay, the
   * telemetry substrate and the network protocol. An inverse-command undo
   * would be a second implementation of every rule — one that has to agree
   * with `applyCommand` about ticks, cooking queues, loot rolls and the
   * turn counter, forever — and a state reached by "un-applying" is a state
   * no log produces. Replaying is slower and cannot be wrong.
   *
   * **It is a cut rather than a truncation**, which is the one thing that
   * changed in §29 U2: settings made *after* the action are not actions, cost
   * nothing and are their own inverse, so they are kept and re-applied over
   * the board the cut leaves. `rules/turns.ts` decides where the floor is and
   * what may be cut.
   *
   * **A setting the cut orphans is dropped, never thrown.** Undo the `build`
   * that made a foundry and the `set-target` aimed at it configures a machine
   * that no longer exists; the reducer is right to refuse it, and the only
   * sane thing to do with it is leave it out. Nothing else may fail — every
   * command before the cut replays over an identical prefix, and everything
   * after it is a setting by construction — so a non-setting refusing on
   * replay is a bug and is left to throw.
   *
   * Answers the squares that changed (`changedSquares`), or **null** when it
   * was refused — the same plan `undoRefusal` read, so a stale button is a
   * no-op and not a crash. A log that will not replay is one of those
   * refusals (`"history-broken"`) and never an exception.
   */
  undo(side: Side): readonly number[] | null {
    if (this.viewer !== null || !modeConfig(this.mode).undo) return null;
    const plan = this.undoPlan(side);
    if (plan === null) return null;
    if (plan.why === "nothing-to-take-back") {
      const upto = this.roundBack(side);
      const was = this.snapshot.state;
      return upto !== null && this.rewindWith(upto) ? changedSquares(was, this.snapshot.state) : null;
    }
    if (plan.why !== null) return null;

    const before = this.snapshot.state;
    // The steps are rebuilt from the same replay rather than from a second
    // one: a cut log is a different history and its memo cannot be salvaged,
    // but nothing here has to pay for it twice.
    const steps = new GameHistory(this.base);
    const { state, log } = cutLog(this.base, this.snapshot.log, plan.cut, (command, next) =>
      steps.push(command, next)
    );

    // The one diff, handed to the tint and to the caller both: the renderer is
    // told what changed rather than guessing it from a shorter log, which is
    // also what `new-game` and `load` do to a log and mean nothing by.
    const changed = changedSquares(before, state);
    this.steps = steps;
    this.commit(state, log, changed);
    return changed;
  }

  /**
   * Take the server's cut (§29 U3): drop these log indices and rebuild.
   *
   * **The room decides and this obeys.** Online the server runs the same
   * `planUndo` and `cutLog` over the same log, so what crosses the wire is the
   * indices rather than the resulting log — one implementation of the cut, and
   * nothing here re-derives which action it was.
   *
   * The log it rebuilds has already replayed at the server, so a refusal here
   * is a **desync and not an orphan**: `IllegalCommandError` propagates and
   * `RoomClient` takes its desynced path. Rebuilt the way `load` does, so a
   * remote undo lands you live — like another side's move.
   */
  applyUndo(dropped: readonly number[]): readonly number[] {
    const cut = new Set(dropped);
    const log = this.snapshot.log.filter((_, index) => !cut.has(index));
    const before = this.snapshot.state;

    const steps = new GameHistory(this.base);
    let state = this.base;
    for (const command of log) {
      state = applyCommand(state, command);
      steps.push(command, state);
    }

    this.steps = steps;
    this.viewer = null;
    const changed = changedSquares(before, state);
    // A fresh array, so the memoised `undoPlan` is invalidated by identity.
    this.commit(state, log, changed);
    return changed;
  }

  /**
   * Go back to log index `upto` and apply one command on the board you land on,
   * as a single step.
   *
   * The generalisation of `undo` that the Time Fluxxer needs (`rewind.ts` decides
   * *where*): undo takes back one command, this takes back a round, and the
   * trailing command is what stops the rewind paying for itself — truncating
   * past the play that caused it would hand the card back.
   *
   * **Nothing is committed until both halves have succeeded.** The replay and
   * the trailing command are worked out against a local `state` and only then
   * written to `this.snapshot`, so a refused command leaves the store exactly
   * as it was. The other order is the dangerous one: rewind first and the
   * player has taken their turn back *and* kept the card.
   *
   * `upto` of -1 is the base state, not a failure — that is where a continued
   * run starts from, `restore` having landed a snapshot with an empty log.
   */
  rewindWith(upto: number, then?: Command): boolean {
    if (upto < -1 || upto >= this.snapshot.log.length) return false;

    const before = this.snapshot.state;
    const log = this.snapshot.log.slice(0, upto + 1);
    const steps = new GameHistory(this.base);
    let state = this.base;
    for (const command of log) {
      state = applyCommand(state, command);
      steps.push(command, state);
    }

    let next = state;
    if (then) {
      try {
        next = applyCommand(state, then);
      } catch (error) {
        if (error instanceof IllegalCommandError) return false;
        throw error;
      }
      steps.push(then, next);
    }
    this.steps = steps;
    this.viewer = null;
    // A rewind takes a round back, so it tints what it took — the same diff
    // undo hands over, for the same reason: the log got shorter and only the
    // store knows that means something.
    this.commit(next, then ? [...log, then] : log, changedSquares(before, next));
    return true;
  }

  /**
   * The Time Fluxxer: hand back the previous round, and spend the card that did it.
   *
   * **The decision lives here because the inputs do.** `planRewind` needs the
   * base and the whole log, and `base` is private for a reason — it is the one
   * thing about the store that cannot be derived, and a caller that could read
   * it could also replay from the wrong place. So the store is asked a question
   * and answers with a refusal or nothing, which is the shape `undoRefusal`
   * already gives the UI.
   *
   * Answers null when it happened. `"rewind-refused"` should be unreachable —
   * `planRewind` has already checked the card is in the deck on the board being
   * landed on, and `burn-card` asks nothing else — but it is a sentence rather
   * than a silent false, because a rewind that quietly does nothing is the
   * exact failure the card's own `refuse` is written to make loud.
   */
  rewindCard(side: Side, instance: number): RewindRefusal | null {
    const plan = planRewind(this.base, this.snapshot.log, side, instance);
    if (!plan.ok) return plan.why;
    return this.rewindWith(plan.upto, { type: "burn-card", instance })
      ? null
      : "rewind-refused";
  }

  toJSON(): string {
    return JSON.stringify({ version: 1, log: this.snapshot.log }, null, 2);
  }
}
